/*
  Heart rate, cadence and power never leave the customer's device.

  A GPX from a watch carries far more than a route. Garmin Connect writes
  `<extensions><ns3:TrackPointExtension><ns3:hr>`, Strava writes the same tree under
  `gpxtpx:`, Wahoo and cycling head units add `<power>`, and every one of those sits
  inside `<extensions>` on the trackpoint. A heart rate is health data: Art 9 of the
  GDPR makes it special-category, the controller here is a Czech s.r.o. so Art 3(1)
  applies whatever country the customer is in, and *receiving* it is already
  processing. Deleting it server-side would be too late, which is why this runs in
  the browser and the server strip (`engine/gpx_sanitize.py`) is only the backstop.

  KEEP-LIST, not a denylist. The first version of this file removed `<extensions>`
  subtrees and nothing else, because that is where vendors are *supposed* to write
  this. The GPX schema does not require it -- `xsd:any ##any` and `anyAttribute` make
  a foreign element or attribute valid anywhere -- and measured against the server
  twin before the fix, `<ns3:hr>171</ns3:hr>` directly under `<trkpt>`, `ns3:hr="171"`
  as an attribute ON `<trkpt>`, and both again in GPX 1.0 all sailed straight through.

  A first attempt narrowed this to "anything in a foreign namespace goes", which closed
  those three and still let through a default-namespaced `<power>250</power>`, an
  unqualified `<trkpt hr="171">`, and a measurement in a comment -- none of which are
  foreign by any test. So the question is inverted all the way: a node survives only if
  `engine/parser.py` reads it. Nine element names, four attributes, no comments, no
  processing instructions, nothing else. `<extensions>` is not special-cased any more;
  it simply is not on the list, which is the point of a list -- the next vendor field to
  be invented is already excluded.

  The GPX namespace is read off the root element, so 1.1, 1.0 and a namespace-less
  export are one comparison rather than three. `xsi:` attributes stay as the one
  structural exception: that vocabulary is `type`/`nil`/`schemaLocation`, none of which
  can carry a measurement, and `schemaLocation` is what keeps `xmlns:xsi` live.

  Loaded as its own asset, before `traceform-preview.js`, and the upload path refuses
  to send anything if this file is missing. That is deliberate: a storefront that
  silently posts raw watch data because an asset 404'd is the exact failure this
  exists to prevent, so the safe direction is a visible error, not a quiet upload.
*/
(function () {
  'use strict';

  // Firefox reports a parse failure by handing back a document whose root is a
  // <parsererror> in this namespace; Chromium and WebKit put the same element in
  // the document without a namespace. Both shapes are checked -- a browser whose
  // failure we did not recognise would look to us like a clean document with no
  // trackpoints, and we would happily "sanitise" it and send it.
  var MOZ_ERROR_NS = 'http://www.mozilla.org/newlayout/xml/parsererror.xml';

  var XMLNS_NS = 'http://www.w3.org/2000/xmlns/';
  // Structural XML, not a vendor schema: the whole vocabulary is type/nil/
  // schemaLocation, none of it can hold a measurement, and `xsi:schemaLocation` is
  // what keeps `xmlns:xsi` live in a Garmin export.
  var XSI_NS = 'http://www.w3.org/2001/XMLSchema-instance';

  /*
    Everything the print does not draw, which is everything `engine/parser.py` does not
    read: nine element names, and no comments or processing instructions. `<extensions>`
    is not named here and does not need to be -- it is simply not on the list, which is
    why a list is the better shape than a rule about `<extensions>`: the next vendor
    field to be invented is already excluded.

    The server twin (`engine/gpx_sanitize.py`) holds the same nine names and a pytest
    guard pinning them to the parser's own xpath. Kept in step by
    `e2e/theme-contract.spec.ts`, which runs BOTH halves over the same documents.
  */
  var KEEP_ELEMENTS = ['gpx', 'trk', 'trkseg', 'rte', 'trkpt', 'rtept', 'ele', 'time', 'name'];
  // `lat`/`lon` are the geometry; `version` and `creator` the GPX schema requires of the
  // root. Everything else goes, INCLUDING unqualified attributes -- `<trkpt hr="171">`
  // is well-formed and schema-valid, and a namespace-only rule never looked at it.
  var KEEP_ATTRIBUTES = ['lat', 'lon', 'version', 'creator'];

  /*
    Walked by hand rather than via `getElementsByTagName('*')`, because that returns
    elements only and a measurement can sit in a comment just as easily. A doomed
    element is not descended into: its whole subtree leaves with it.
  */
  function unwantedNodes(doc) {
    var root = doc.documentElement;
    var found = [];
    (function walk(node) {
      var kids = node.childNodes;
      for (var i = 0; i < kids.length; i++) {
        var n = kids[i];
        if (n.nodeType === 1) {
          if (n.namespaceURI !== root.namespaceURI || KEEP_ELEMENTS.indexOf(n.localName) === -1) {
            found.push(n);
          } else {
            walk(n);
          }
        } else if (n.nodeType === 8 || n.nodeType === 7) {
          found.push(n);
        }
      }
    })(root);
    return found;
  }

  /*
    The attribute half, and the cheaper hiding place of the two: `<trkpt lat=".."
    lon=".." hr="171">` is well-formed, schema-valid, renders identically, and no
    element-level rule can see it. Unqualified is NOT a free pass -- that was the
    assumption the namespace-only version made, and `hr` carries no prefix at all.
  */
  function unwantedAttributes(doc) {
    var root = doc.documentElement;
    var all = doc.getElementsByTagName('*');
    var found = [];
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      for (var a = 0; a < el.attributes.length; a++) {
        var attr = el.attributes[a];
        // Namespace declarations are `dropUnusedPrefixes`' job, not this one's.
        if (attr.namespaceURI === XMLNS_NS || attr.namespaceURI === XSI_NS) continue;
        var ownNamespace = !attr.namespaceURI || attr.namespaceURI === root.namespaceURI;
        if (ownNamespace && KEEP_ATTRIBUTES.indexOf(attr.localName) !== -1) continue;
        found.push({ el: el, ns: attr.namespaceURI, name: attr.localName });
      }
    }
    return found;
  }

  /*
    A namespace declaration outlives the elements that used it. Removing every
    <extensions> leaves `xmlns:ns3=".../TrackPointExtension/v1"` sitting on the root,
    which holds no measurement -- so it is not Art 9 data -- but does announce that
    this run came off a device that records heart rates, and it is neither geometry
    nor time, which is all we said we would send.

    A declaration counts as used if any element OR any attribute still resolves to
    it. The attribute half is not optional: `xsi:schemaLocation` is the only thing
    keeping `xmlns:xsi` alive in a Garmin export, and dropping it would strip a
    declaration the document is still using.
  */
  function dropUnusedPrefixes(doc) {
    var root = doc.documentElement;
    var live = {};
    var all = doc.getElementsByTagName('*');
    for (var i = 0; i < all.length; i++) {
      if (all[i].namespaceURI) live[all[i].namespaceURI] = true;
      var attrs = all[i].attributes;
      for (var a = 0; a < attrs.length; a++) {
        if (attrs[a].namespaceURI && attrs[a].namespaceURI !== XMLNS_NS) {
          live[attrs[a].namespaceURI] = true;
        }
      }
    }
    var declared = [];
    for (var d = 0; d < root.attributes.length; d++) {
      var attr = root.attributes[d];
      if (attr.name.indexOf('xmlns:') === 0) declared.push(attr);
    }
    for (var k = 0; k < declared.length; k++) {
      if (!live[declared[k].value]) {
        root.removeAttributeNS(XMLNS_NS, declared[k].name.slice(6));
      }
    }
  }

  function parseError(doc) {
    if (!doc || !doc.documentElement) return true;
    if (doc.documentElement.nodeName === 'parsererror') return true;
    if (doc.getElementsByTagNameNS(MOZ_ERROR_NS, 'parsererror').length > 0) return true;
    if (doc.getElementsByTagName('parsererror').length > 0) return true;
    return false;
  }

  /*
    Returns the GPX text with every <extensions> element removed.

    Throws on anything it cannot read rather than returning the input. The caller
    turns that into the same "we can't read that file" the customer already sees for
    a corrupt upload, and the file stays on their device. Returning the original
    would be the one outcome that defeats the whole function.
  */
  function sanitizeGpxText(text) {
    if (typeof text !== 'string' || text.length === 0) throw new Error('gpx_unreadable');
    var doc = new DOMParser().parseFromString(text, 'application/xml');
    if (parseError(doc)) throw new Error('gpx_unreadable');

    var doomed = unwantedNodes(doc);
    // Deepest last, removed first: an element nested inside another doomed one is
    // detached along with its parent, and asking a detached node for its parent
    // answers null rather than throwing.
    for (var j = doomed.length - 1; j >= 0; j--) {
      var el = doomed[j];
      if (el.parentNode) el.parentNode.removeChild(el);
    }

    var strays = unwantedAttributes(doc);
    for (var k = 0; k < strays.length; k++) {
      strays[k].el.removeAttributeNS(strays[k].ns, strays[k].name);
    }

    dropUnusedPrefixes(doc);

    var out = new XMLSerializer().serializeToString(doc);
    // Cheap self-check, kept because the cost of being wrong here is health data on
    // someone else's disk. If a browser ever hands back a tree the removal missed,
    // this refuses rather than uploads. It asks the same two questions the removal
    // did, so a rule that stops matching stops passing -- a check that only counted
    // `<extensions>` would have called every leak above a clean document.
    if (unwantedNodes(doc).length > 0) throw new Error('gpx_unreadable');
    if (unwantedAttributes(doc).length > 0) throw new Error('gpx_unreadable');
    return out;
  }

  window.TraceformGpxSanitize = { sanitizeGpxText: sanitizeGpxText };
})();
